import { Suspense, lazy, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Spin } from 'antd';

import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import classnames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import messages from '../../constants/messages';

import { useModal } from '../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';

import { fetchGeofences } from '../../redux/actions/geofence';
import { createRotation, deleteRotation, fetchRotations, updateRotation } from '../../redux/actions/geofence-rotations';

import styles from './styles.module.scss';

import { CALENDAR_VIEWS, DEFAULT_CALENDAR_HEIGHT, DOUBLE_CLICK_DELAY } from '../Calendar/constants';
import RotationForm from '../Grazing/RotationForm';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../action_notifier';
import CustomToolbar from './CustomToolbar';

const AppModal = lazy(() => import('../AppModal'));
const RotationDetailsModal = lazy(() => import('./RotationDetailsModal'));
const UnsavedChangesModal = lazy(() => import('../../components/ui/UnsavedChangesModal'));

dayjs.extend(utc);

const PasturesCalendar = ({
  calendarHeight = DEFAULT_CALENDAR_HEIGHT,
  calendarSizes,
  className,
  isLoading = false,
  resources,
  slotDuration = '24:00:00'
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const formCreateRef = useRef(null);
  const formRef = useRef(null);

  const { currentSelectedDate } = useSelector(state => state.calendar);
  const { data: geofences } = useSelector(state => state.geofence);

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();
  const [createModalVisible, openCreateModal, closeCreateModal] = useModal();
  const [viewModalVisible, openViewModal, closeViewModal] = useModal();

  const [currentView, setCurrentView] = useState(CALENDAR_VIEWS.RESOURCE_MONTH);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [submittable, setSubmittable] = useState(false);
  const [newRotation, setNewRotation] = useState({
    geofenceId: null,
    startDate: null,
    endDate: null
  });
  const [selectedRotation, setSelectedRotation] = useState(null);

  const calendarRef = useRef();
  const lastSlotClickTimeRef = useRef(null);

  const handleCreateRotations = useCallback(async () => {
    try {
      const data = await formCreateRef.current?.validateFields();

      await dispatch(createRotation({ ...data, geofence_id: newRotation.geofenceId }));
      await fetchData();

      successToastHandler(messages.CREATED);
      closeCreateModal();
      setNewRotation({ geofenceId: null, startDate: null, endDate: null });
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA);
    }
  }, [formCreateRef, newRotation]);

  async function fetchData(query = '') {
    try {
      await Promise.all([dispatch(fetchGeofences({ query }))]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.gotoDate(currentSelectedDate);
    }
  }, [currentSelectedDate]);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.changeView(currentView);
    }
  }, [currentView]);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.render();
    }
  }, [calendarSizes]);

  const formatEvents = useCallback(
    geofences => {
      return geofences.flatMap(geofence =>
        geofence.rotations.map(rotation => ({
          ...rotation,
          title: rotation.rotation_name,
          start: dayjs(rotation.rotation_start_date).toDate(),
          end: dayjs(rotation.rotation_end_date).toDate(),
          resourceId: geofence.id
        }))
      );
    },
    [newRotation]
  );

  const onSelectEvent = ({ event }) => {
    setSelectedRotation({
      ...event.extendedProps,
      assigned_groups: event.extendedProps.assigned_groups?.map(g => g.value),
      id: event?._def?.publicId
    });
    setIsEditMode(false);
    openViewModal();
  };

  const parseDuration = durationStr => {
    const [hours, minutes, seconds] = durationStr.split(':').map(Number);

    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  };

  const isSingleSlotSelected = slot => {
    const durationMs = parseDuration(slotDuration);

    const start = slot.start.getTime();
    const end = slot.end.getTime();

    return end - start <= durationMs;
  };

  const onSelectSlot = async slot => {
    const isSingleSlot = isSingleSlotSelected(slot);
    const now = Date.now();

    if (isSingleSlot && now - lastSlotClickTimeRef.current < DOUBLE_CLICK_DELAY) {
      lastSlotClickTimeRef.current = null;
    } else if (isSingleSlot) {
      lastSlotClickTimeRef.current = now;

      return;
    }

    setNewRotation({ geofenceId: +slot.resource.id, rotation_start_date: slot.start, rotation_end_date: slot.end });
    openCreateModal();
  };

  const handleViewChange = view => {
    setCurrentView(view);
    setSelectedCalendarDate(null);
  };

  const onView = view => {
    closeCreateModal();
    closeViewModal();
    setCurrentView(view);
  };

  const options = [
    {
      label: 'Week',
      value: CALENDAR_VIEWS.RESOURCE_WEEK
    },
    {
      label: 'Month',
      value: CALENDAR_VIEWS.RESOURCE_MONTH
    },
    {
      label: 'Year',
      value: CALENDAR_VIEWS.RESOURCE_YEAR
    }
  ];

  const moveEvent = async ({ event, revert }) => {
    const isStartDateSame = dayjs(event.start).isSame(dayjs(event?.extendedProps?.rotation_start_date), 'day');

    if (!isStartDateSame && event.start < new Date()) {
      revert();
      warnToastHandler(messages.CANT_MOVE_AN_EVENT_INTO_THE_PAST);

      return;
    }

    try {
      await dispatch(
        updateRotation({
          id: event?._def?.publicId,
          data: {
            geofence_id: event?._def?.resourceIds?.at(0),
            rotation_start_date: event.start?.toISOString(),
            rotation_end_date: event.end?.toISOString(),
            rotation_name: event?.extendedProps?.rotation_name
          }
        })
      ).unwrap();

      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
      revert();
    }
  };

  const handleEditRotation = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const handleUpdateRotation = useCallback(async () => {
    try {
      const data = await formRef.current?.validateFields();

      await dispatch(
        updateRotation({
          id: selectedRotation?.id,
          data: { ...data, forage_species: selectedRotation?.forage_species }
        })
      ).unwrap();
      await dispatch(fetchRotations()).unwrap();
      await fetchData();

      setIsFormDirty(false);
      successToastHandler(messages.UPDATED);
      closeViewModal();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }
  }, [formRef, selectedRotation]);

  const handleDeleteRotation = async () => {
    try {
      await dispatch(deleteRotation(selectedRotation?.id)).unwrap();
      await dispatch(fetchRotations()).unwrap();
      await fetchData();

      setIsFormDirty(false);
      closeArchiveModal();
      closeViewModal();
      successToastHandler(messages.DELETED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }
  };

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await handleUpdateRotation();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        formRef.current?.resetFields();
        setIsFormDirty(false);
        closeViewModal();
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : closeViewModal();
  }, [isFormDirty]);

  return (
    <>
      {NavigationPrompt}

      <div className={classnames(styles['calendar-component'], className)}>
        <CustomToolbar
          currentView={currentView}
          options={options}
          selectedCalendarDate={selectedCalendarDate}
          handleViewChange={handleViewChange}
          onView={onView}
        />
        <div className="position-relative">
          <div style={{ position: 'relative' }}>
            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255,255,255,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 999
                }}>
                <Spin tip="Loading geofences..." fullscreen={true} />
              </div>
            )}
            <FullCalendar
              dayHeaderFormat={{ weekday: 'short' }}
              displayEventTime={false}
              droppable={true}
              editable={true}
              headerToolbar={false}
              nowIndicator={true}
              selectable={true}
              events={formatEvents(geofences)}
              height={calendarHeight}
              contentHeight="auto"
              initialView={CALENDAR_VIEWS.RESOURCE_MONTH}
              plugins={[interactionPlugin, resourceTimelinePlugin]}
              ref={calendarRef}
              resourceAreaHeaderContent="Pastures"
              resourceAreaWidth="15%"
              resources={resources}
              slotDuration={slotDuration}
              slotLabelFormat={[
                { month: 'short', year: 'numeric' }, // top level of text,
                {
                  weekday: 'short',
                  month: 'numeric',
                  day: 'numeric',
                  hour12: false
                }
              ]}
              views={{
                resourceTimelineCustomYear: {
                  type: 'resourceTimeline',
                  slotDuration: { weeks: 1 },
                  visibleRange: currentDate => {
                    const year = currentDate.getFullYear();
                    const start = new Date(year, 0, 1);
                    const end = new Date(year + 1, 0, 1);

                    return { start, end };
                  },
                  slotLabelFormat: [{ month: 'short', year: 'numeric' }, { week: 'numeric' }],
                  slotLabelInterval: { weeks: 1 },
                  weekNumbers: true
                }
              }}
              eventClick={onSelectEvent}
              eventDrop={moveEvent}
              eventResize={moveEvent}
              select={onSelectSlot}
            />
          </div>
        </div>

        <Suspense fallback={null}>
          <AppModal
            isOpen={createModalVisible}
            confirmButtonText="Add"
            confirmButtonDisabled={!submittable}
            title="Add Rotation"
            handleCancel={closeCreateModal}
            handleConfirm={handleCreateRotations}>
            <RotationForm
              className="py-4"
              formRef={formCreateRef}
              setSubmittable={setSubmittable}
              initialRotationValues={newRotation}
            />
          </AppModal>

          <AppModal
            isOpen={archiveModalVisible}
            confirmButtonColor="danger"
            confirmButtonText="Archive"
            title="Archive Rotation"
            handleCancel={closeArchiveModal}
            handleConfirm={handleDeleteRotation}>
            <div className="py-4">{messages.ARCHIVE_CONFIRMATION('rotation')}</div>
          </AppModal>

          <RotationDetailsModal
            formRef={formRef}
            isEditMode={isEditMode}
            isOpen={viewModalVisible}
            selectedRotation={selectedRotation}
            handleCancel={handleCancel}
            handleDelete={openArchiveModal}
            handleEdit={handleEditRotation}
            handleUpdate={handleUpdateRotation}
            onChange={setIsFormDirty}
          />

          <UnsavedChangesModal
            isOpen={modalProps.visible}
            handleCancel={modalProps.onCancel}
            handleConfirm={modalProps.onSave}
            handleDiscard={modalProps.onDiscard}
          />
        </Suspense>
      </div>
    </>
  );
};

export default memo(PasturesCalendar);
